// react
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute } from '@tanstack/react-router';

// material ui icons
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// material ui
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

// api
import { useGetDatahubAccountByIdQuery } from '@/api/datahub-integration/datahubIntegrationApiSlice';

// components
import DatahubGuideDialog from '@/features/datahub-integration/DatahubGuideDialog';
import DatahubIntegrationAccountCard from '@/features/datahub-integration/DatahubIntegrationAccountCard';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';
import ErrorBoundary from '@/routes/-ErrorBoundary';

import dayjs from 'dayjs';

interface DatahubIntegrationAccount {
  CustomerCVR: string;
}

export const Route = createFileRoute('/$accountId/_user/datahub-integration/')({
  component: RouteComponent,
  errorComponent: ErrorBoundary,
});

function RouteComponent() {
  const { t } = useTranslation('datahub-integration');

  const selectedAccount = useSelector(getSelectedAccount);
  const dispatch = useDispatch();

  const selectedAccountId = selectedAccount?.AccountId;
  const hasSelectedAccountId =
    selectedAccountId !== undefined && selectedAccountId !== null;

  const {
    data: accountData,
    isLoading,
    isError,
  } = useGetDatahubAccountByIdQuery(selectedAccountId, {
    skip: !hasSelectedAccountId,
  });
  const account = accountData as DatahubIntegrationAccount[] | undefined;
  const [guideOpen, setGuideOpen] = useState(false);
  const getGrantLink = () => {
    if (!hasSelectedAccountId) return '';

    // dates are YYYY-MM-DD
    const fromDate = dayjs().subtract(3, 'year').format('YYYY-MM-DD');
    const toDate = dayjs().add(3, 'year').format('YYYY-MM-DD');
    return `https://eloverblik.dk/power-of-attorney?thirdPartyId=23C949D6-394A-4001-94EC-B4F55E9CF850&fromDate=${fromDate}&toDate=${toDate}&customerKey=${selectedAccountId}`;
  };

  const handleExternalGrantLink = () => {
    window.open(getGrantLink(), '_blank');
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(getGrantLink());
    dispatch(
      showSnackbar({
        message: t('give-access.link-copied'),
        severity: 'success',
      }),
    );
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Stack spacing={3}>
        {/* Create Integration Card */}
        <Card>
          <CardHeader
            title={t('create-integration.title')}
            action={
              <IconButton onClick={() => setGuideOpen(true)} color="primary">
                <HelpOutlineIcon />
              </IconButton>
            }
          />
          <CardContent>
            <Stack spacing={2}>
              <Grid container spacing={2} alignItems="stretch">
                <Grid
                  size={{
                    xs: 12,
                    md: 5
                  }}>
                  <Stack spacing={2} sx={{ height: '100%' }}>
                    <ListItemText
                      primary={t('give-access.title')}
                      secondary={t('give-access.description')}
                    />
                    <Box sx={{ flexGrow: 1 }} />
                    <Box>
                      <Button
                        variant="contained"
                        disabled={!hasSelectedAccountId}
                        onClick={() => handleExternalGrantLink()}
                      >
                        {t('give-access.button')}
                      </Button>
                    </Box>
                  </Stack>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 2
                  }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      height: '100%',
                      minHeight: { xs: '40px', md: 'auto' },
                    }}
                  >
                    <Divider
                      orientation="vertical"
                      sx={{
                        display: { xs: 'none', md: 'block' },
                        height: '100%',
                        position: 'absolute',
                      }}
                    />
                    <Divider
                      sx={{
                        display: { xs: 'block', md: 'none' },
                        width: '100%',
                        position: 'absolute',
                      }}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        bgcolor: 'background.paper',
                        px: 2,
                        zIndex: 1,
                      }}
                    >
                      {t('give-access.or')}
                    </Typography>
                  </Box>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 5
                  }}>
                  <Stack spacing={2} sx={{ height: '100%' }}>
                    <ListItemText
                      primary={t('give-access.copy-title')}
                      secondary={t('give-access.copy-description')}
                    />
                    <Box sx={{ flexGrow: 1 }} />
                    <Box>
                      <Button
                        variant="outlined"
                        disabled={!hasSelectedAccountId}
                        startIcon={<ContentCopyIcon />}
                        onClick={handleCopyLink}
                      >
                        {t('give-access.copy-button')}
                      </Button>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </Stack>
          </CardContent>
        </Card>

        {/* Existing Integrations Card */}
        <Card>
          <CardHeader
            title={t('integrations-list.title')}
            subheader={t('integrations-list.subtitle')}
          />
          <CardContent>
            {isLoading ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {!isError && account?.length > 0 ? (
                  <List sx={{ width: '100%' }}>
                    {account.map((a) => {
                      return (
                        <DatahubIntegrationAccountCard
                          key={a.CustomerCVR}
                          account={a}
                        />
                      );
                    })}
                  </List>
                ) : (
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    {t('integrations-list.no-integrations')}
                  </Typography>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Guide Dialog */}
        <DatahubGuideDialog
          open={guideOpen}
          onClose={() => setGuideOpen(false)}
        />
      </Stack>
    </Container>
  );
}
